/**
 * frontend/src/pages/Settings.jsx
 * =================================
 * Winery settings: C7 credentials, billing, sync controls.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { authApi, billingApi, syncApi } from '../lib/api'

function Section({ title, description, children }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden mb-6">
      <div className="px-6 py-4 border-b border-gray-800">
        <h2 className="font-semibold text-white text-sm">{title}</h2>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  )
}

const inputCls =
  'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500'

export default function Settings() {
  const qc = useQueryClient()

  // C7 settings form
  const [c7Form, setC7Form] = useState({
    c7_tenant_slug: '',
    c7_app_id: '',
    c7_app_secret: '',
  })
  const [c7Feedback, setC7Feedback] = useState(null)
  const [skipValidation, setSkipValidation] = useState(false)

  const c7Mutation = useMutation({
    mutationFn: ({ data, validate }) => authApi.updateC7Settings(data, { validate }),
    onSuccess: (_, { validate }) =>
      setC7Feedback({ type: 'success', msg: validate ? 'Commerce7 credentials saved and validated.' : 'Commerce7 credentials saved (validation skipped).' }),
    onError: (err) =>
      setC7Feedback({
        type: 'error',
        msg: err.response?.data?.detail || 'Failed to save credentials.',
      }),
  })

  // Billing
  const { data: billing } = useQuery({
    queryKey: ['billing-status'],
    queryFn: () => billingApi.status().then((r) => r.data),
  })

  const portalMutation = useMutation({
    mutationFn: () => billingApi.portal(),
    onSuccess: (res) => window.open(res.data.url, '_blank'),
  })

  // Sync
  const { data: syncLogs = [] } = useQuery({
    queryKey: ['sync-logs'],
    queryFn: () => syncApi.logs(5).then((r) => r.data),
    refetchInterval: 10_000,
  })

  const syncMutation = useMutation({
    mutationFn: (type) => syncApi.trigger({ sync_type: type }),
    onSuccess: () => qc.invalidateQueries(['sync-logs']),
  })

  const PLAN_LABELS = {
    trial: 'Trial',
    starter: 'Starter',
    growth: 'Growth',
    pro: 'Pro',
  }
  const STATUS_COLORS = {
    success: 'text-green-400',
    error: 'text-red-400',
    running: 'text-blue-400',
    pending: 'text-gray-400',
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-xl font-semibold text-white mb-6">Settings</h1>

      {/* Commerce7 Credentials */}
      <Section
        title="Commerce7 Connection"
        description="Your C7 App ID and Secret are encrypted at rest with AES-256."
      >
        <div className="space-y-4">
          {c7Feedback && (
            <div className={`text-sm rounded-lg px-4 py-3 ${
              c7Feedback.type === 'error'
                ? 'bg-red-900/40 border border-red-800 text-red-300'
                : 'bg-green-900/40 border border-green-800 text-green-300'
            }`}>
              {c7Feedback.msg}
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-400 mb-1">Tenant Slug</label>
            <input
              className={inputCls}
              value={c7Form.c7_tenant_slug}
              onChange={(e) => setC7Form((f) => ({ ...f, c7_tenant_slug: e.target.value }))}
              placeholder="ridgeline-winery"
            />
            <p className="text-xs text-gray-600 mt-1">
              The tenant slug from your C7 App Dev Center (not the app name).
            </p>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">App ID</label>
            <input
              className={inputCls}
              value={c7Form.c7_app_id}
              onChange={(e) => setC7Form((f) => ({ ...f, c7_app_id: e.target.value }))}
              placeholder="Paste your C7 App ID"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">App Secret</label>
            <input
              type="password"
              className={inputCls}
              value={c7Form.c7_app_secret}
              onChange={(e) => setC7Form((f) => ({ ...f, c7_app_secret: e.target.value }))}
              placeholder="Paste your C7 App Secret"
            />
          </div>

          <div className="bg-gray-800/50 rounded-lg px-4 py-3 text-xs text-gray-500 space-y-1">
            <p className="font-medium text-gray-400">C7 App Dev Center setup:</p>
            <p>1. Create a Private App → set App URL to your chekn.io subdomain</p>
            <p>2. Placement: Admin Panel (iframe)</p>
            <p>3. Add your winery as a Client at the bottom</p>
            <p>4. Request scopes: <code className="text-violet-400">reservation:read order:read customer:read</code></p>
          </div>

          <button
            onClick={() => { setC7Feedback(null); c7Mutation.mutate({ data: c7Form, validate: !skipValidation }) }}
            disabled={
              !c7Form.c7_tenant_slug ||
              !c7Form.c7_app_id ||
              !c7Form.c7_app_secret ||
              c7Mutation.isPending
            }
            className="bg-violet-700 hover:bg-violet-600 disabled:opacity-40 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
          >
            {c7Mutation.isPending ? (skipValidation ? 'Saving…' : 'Validating…') : (skipValidation ? 'Save' : 'Save & Validate')}
          </button>

          <label className="flex items-center gap-2 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={skipValidation}
              onChange={(e) => setSkipValidation(e.target.checked)}
              className="accent-violet-600 w-3.5 h-3.5"
            />
            <span className="text-xs text-gray-500">Skip validation (use if app is pending C7 approval)</span>
          </label>
        </div>
      </Section>

      {/* Sync */}
      <Section
        title="Data Sync"
        description="Manually trigger Commerce7 data sync or view recent sync logs."
      >
        <div className="flex gap-3 mb-5">
          {['full', 'reservations', 'orders'].map((type) => (
            <button
              key={type}
              onClick={() => syncMutation.mutate(type)}
              disabled={syncMutation.isPending}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-gray-300 text-xs rounded-lg capitalize transition-colors"
            >
              {syncMutation.isPending ? '…' : `Sync ${type}`}
            </button>
          ))}
        </div>

        {syncLogs.length > 0 && (
          <div className="space-y-2">
            {syncLogs.map((log) => (
              <div key={log.id} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className={STATUS_COLORS[log.status] || 'text-gray-400'}>
                    ● {log.status}
                  </span>
                  <span className="text-gray-500 capitalize">{log.sync_type}</span>
                </div>
                <div className="text-gray-500">
                  {log.records_synced} records
                  {log.error_message && (
                    <span className="text-red-400 ml-2" title={log.error_message}>
                      ⚠
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Billing */}
      <Section title="Billing & Plan">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-white">
              Current plan:{' '}
              <span className="text-violet-400">
                {PLAN_LABELS[billing?.plan_tier] || '—'}
              </span>
            </div>
            {billing?.trial_ends_at && (
              <div className="text-xs text-gray-500 mt-0.5">
                Trial ends: {new Date(billing.trial_ends_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <button
            onClick={() => portalMutation.mutate()}
            disabled={portalMutation.isPending || !billing?.has_subscription}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-gray-300 text-sm rounded-lg transition-colors"
          >
            {portalMutation.isPending ? 'Loading…' : 'Manage Billing'}
          </button>
        </div>
      </Section>
    </div>
  )
}
